import React, { useState, useEffect } from 'react';

const ProjectsSection = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [viewMode, setViewMode] = useState('showcase'); // 'showcase', 'grid', or 'timeline'
  const [isLoading, setIsLoading] = useState(true);
  
  // Simulate loading state
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Project data
  const projects = [
    {
      id: 'email-spam',
      title: 'Email Spam Classifier',
      description: 'Machine learning model that identifies spam emails with high accuracy using natural language processing techniques.',
      longDescription: 'This project implements a machine learning classifier that can accurately identify spam emails. It uses various NLP techniques like TF-IDF vectorization and feature engineering combined with a Random Forest classifier. The model achieves over 97% accuracy on test data.',
      tags: ['Python', 'Machine Learning', 'NLP', 'scikit-learn'],
      category: 'Machine Learning',
      featured: true,
      demoUrl: null,
      repoUrl: 'https://github.com/ARNAB814/Spam-Email-Classifier',
      media: {
        type: 'image',
        url: '/assets/images/project-spam.jpg',
        alt: 'Email Spam Classifier interface'
      },
      highlights: [
        'Implemented SMOTE for synthetic minority oversampling',
        'Used n-gram analysis and feature importance ranking',
        'Applied threshold tuning and cross-validation'
      ],
      challenges: [
        'Handling imbalanced dataset with more non-spam than spam emails',
        'Optimizing feature extraction for better performance',
        'Reducing false positives while maintaining high recall'
      ]
    },
    {
      id: 'game-of-life',
      title: 'Game of Life',
      description: 'Interactive implementation of Conway\'s Game of Life cellular automaton with customizable rules and patterns.',
      longDescription: 'A complete implementation of Conway\'s Game of Life with an interactive UI built in Java. Users can create custom patterns, adjust simulation speed, and modify the rules of the automaton. The project demonstrates object-oriented design principles and efficient algorithms for cellular automata.',
      tags: ['Java', 'Algorithms', 'Simulation', 'UI'],
      category: 'Algorithms',
      featured: true,
      demoUrl: null,
      repoUrl: 'https://github.com/ARNAB814/Game-of-Life',
      media: {
        type: 'image',
        url: '/assets/images/project-gameoflife.jpg',
        alt: 'Game of Life simulation'
      },
      highlights: [
        'Used efficient neighbor checking algorithms',
        'Implemented sparse matrix representation for large patterns',
        'Designed a user-friendly UI with zoom and pan capabilities'
      ],
      challenges: [
        'Optimizing performance for large grid sizes',
        'Implementing wraparound boundary conditions',
        'Creating an intuitive user interface for pattern editing'
      ]
    },
    {
      id: 'cant-stop',
      title: 'Can\'t Stop Board Game',
      description: 'Digital adaptation of the classic \'Can\'t Stop\' board game with AI opponents and online multiplayer.',
      longDescription: 'A complete digital version of the popular board game \'Can\'t Stop\' built with Java Swing. Features include AI opponents with different difficulty levels, customizable game rules, and a robust turn-based system. The game implements the complete ruleset of the original board game with additional digital enhancements.',
      tags: ['Java', 'Swing', 'Game Development', 'AI'],
      category: 'Game Development',
      featured: true,
      demoUrl: null,
      repoUrl: 'https://github.com/ARNAB814/Cant-Stop',
      media: {
        type: 'image',
        url: '/assets/images/project-cantstop.jpg',
        alt: 'Can\'t Stop digital board game'
      },
      highlights: [
        'Used minimax algorithm with alpha-beta pruning for AI',
        'Developed comprehensive test suite to verify game rules',
        'Designed a visually appealing and user-friendly board representation'
      ],
      challenges: [
        'Developing AI opponents with varying strategies',
        'Implementing the complex board game rules accurately',
        'Creating an intuitive user interface for game state visualization'
      ]
    },
    {
      id: 'knights-tour',
      title: 'Knight\'s Tour Solver',
      description: 'Algorithm visualization for solving the Knight\'s Tour problem on chess boards of various sizes.',
      longDescription: 'An interactive visualizer for the Knight\'s Tour problem that finds a sequence of moves for a knight to visit every square on a chess board exactly once. Users can choose different board sizes, starting positions, and watch the algorithm work in real-time with adjustable speeds.',
      tags: ['JavaScript', 'Algorithms', 'Visualization', 'Backtracking'],
      category: 'Algorithms',
      featured: false,
      demoUrl: null,
      repoUrl: 'https://github.com/ARNAB814/Knights-Tour',
      media: {
        type: 'image',
        url: '/assets/images/project-knight.jpg',
        alt: 'Knight\'s Tour visualization'
      },
      highlights: [
        'Implemented Warnsdorff\'s heuristic for optimized path finding',
        'Created step-by-step animation with adjustable speed',
        'Added visual cues to show possible moves and decision making'
      ],
      challenges: [
        'Implementing efficient backtracking algorithm for large boards',
        'Creating a visually engaging and informative animation',
        'Optimizing the solution path finding with heuristics'
      ]
    },
    {
      id: 'portfolio',
      title: 'Portfolio Website',
      description: 'Personal portfolio website built with Astro and React, featuring a tech-focused design and interactive components.',
      longDescription: 'A modern portfolio website built using Astro framework with React components. The site features a tech-focused design theme with interactive elements, responsive layouts, and optimized performance. It showcases projects, work experience, courses, and creative content.',
      tags: ['Astro', 'React', 'TailwindCSS', 'Web Development'],
      category: 'Web Development',
      featured: false,
      demoUrl: 'https://arunav.netlify.com',
      repoUrl: 'https://github.com/ARNAB814/Portfolio',
      media: {
        type: 'image',
        url: '/assets/images/project-portfolio.jpg',
        alt: 'Portfolio website screenshot'
      },
      highlights: [
        'Developed a tech-focused theme with consistent visual elements',
        'Used React for interactive components with Astro static generation',
        'Implemented modern web performance best practices'
      ],
      challenges: [
        'Creating a cohesive design that highlights technical projects',
        'Implementing advanced interactive components',
        'Optimizing performance and accessibility'
      ]
    },
    {
      id: 'battleship',
      title: 'Battleship Engine',
      description: 'Online multiplayer battleship game with custom game modes and realistic sound effects.',
      longDescription: 'A complete online battleship game implementation with both single-player and multiplayer modes. Features include customizable ship layouts, various game modes with different rules, and a chat system for multiplayer games. The game also includes realistic sound effects and animations for an immersive experience.',
      tags: ['JavaScript', 'HTML', 'CSS', 'Game Development'],
      category: 'Game Development',
      featured: false,
      demoUrl: null,
      repoUrl: null,
      media: {
        type: 'image',
        url: '/assets/images/project-battleship.jpg',
        alt: 'Battleship game interface'
      },
      highlights: [
        'Used a state machine approach for game flow management',
        'Implemented responsive CSS grid layouts',
        'Developed multi-level AI with different strategies'
      ],
      challenges: [
        'Implementing efficient game state management',
        'Creating a responsive design that works on all devices',
        'Developing a challenging AI opponent for single-player mode'
      ]
    },
    {
      id: 'ninja-adventure',
      title: 'Ninja Adventure',
      description: '2D top-down action RPG game developed in C++ with the SFML framework.',
      longDescription: 'A 2D action RPG game featuring a ninja character on an adventure through various environments. The game includes combat mechanics, inventory management, and a quest system. Built using C++ and the SFML framework, it demonstrates object-oriented game design principles.',
      tags: ['C++', 'SFML', 'Game Development', 'Object-Oriented Design'],
      category: 'Game Development',
      featured: false,
      demoUrl: null,
      repoUrl: null,
      media: {
        type: 'image',
        url: '/assets/images/project-ninja.jpg',
        alt: 'Ninja Adventure game screenshot'
      },
      highlights: [
        'Developed a modular component-based architecture',
        'Implemented physics-based movement with collision detection',
        'Created a behavior tree system for enemy AI'
      ],
      challenges: [
        'Creating a flexible entity-component system',
        'Implementing smooth character movement and combat',
        'Designing engaging levels and enemy AI'
      ]
    },
    {
      id: 'sudoku-solver',
      title: 'Sudoku Solver',
      description: 'Sudoku puzzle solver and generator with visualization of solving algorithms.',
      longDescription: 'A comprehensive Sudoku application that can generate puzzles of varying difficulty, solve any valid Sudoku puzzle, and visualize the solving process step by step. The application includes multiple solving algorithms and allows users to play Sudoku with hints and validation.',
      tags: ['Python', 'Algorithms', 'UI/UX'],
      category: 'Algorithms',
      featured: false,
      demoUrl: null,
      repoUrl: 'https://github.com/ARNAB814/Sudoku',
      media: {
        type: 'image',
        url: '/assets/images/project-sudoku.jpg',
        alt: 'Sudoku solver application'
      },
      highlights: [
        'Used constraint propagation with backtracking',
        'Implemented difficulty rating based on required techniques',
        'Developed step-by-step visual explanation of solving process'
      ],
      challenges: [
        'Implementing efficient backtracking for puzzle solving',
        'Generating puzzles with unique solutions of controllable difficulty',
        'Creating an intuitive visualization of solving steps'
      ]
    }
  ];
  
  // Get all unique categories
  const categories = ['all', ...new Set(projects.map(project => project.category))];
  
  // Filter projects based on category and search term
  const filteredProjects = projects.filter(project => {
    const matchesCategory = activeFilter === 'all' || project.category === activeFilter;
    const matchesSearch = searchTerm === '' || 
      project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesCategory && matchesSearch;
  });
  
  // Get featured projects
  const featuredProjects = projects.filter(project => project.featured);
  
  // Get details of selected project
  const getSelectedProject = () => projects.find(p => p.id === selectedProject);
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Handle category filter change
  const handleFilterChange = (category) => {
    setActiveFilter(category);
    setSelectedProject(null);
  };
  
  // Handle project selection
  const handleProjectSelect = (projectId) => {
    setSelectedProject(projectId === selectedProject ? null : projectId);
  };
  
  // Close project details modal
  const closeProjectDetails = () => {
    setSelectedProject(null);
  };
  
  // Change view mode
  const changeViewMode = (mode) => {
    setViewMode(mode);
    setSelectedProject(null);
  };
  
  return (
    <div className="py-16 relative">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full opacity-5 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCI+CjxwYXRoIGQ9Ik0wIDBhMzAgMzAgMCAwIDEgNjAgMEgweiIgZmlsbD0iIzMzMyIvPgo8L3N2Zz4=')]"></div>
        <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-yellow-500/10 rounded-full filter blur-3xl"></div>
        <div className="absolute top-40 -left-20 w-72 h-72 bg-blue-500/10 rounded-full filter blur-3xl"></div>
      </div>
      
      {/* Section header */}
      <div className="text-center mb-12 relative">
        <div className="inline-block mb-3">
          <div className="relative px-3 py-1 text-sm font-medium text-purple-600 dark:text-purple-400 bg-purple-100 dark:bg-purple-900/30 rounded-full">
            <span className="relative z-10">My Work</span>
          </div>
        </div>
        <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900 dark:text-white bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-600 dark:from-purple-400 dark:to-blue-400">Creative Projects</h2>
        <p className="text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
          A collection of my technical projects across various domains and technologies
        </p>
      </div>
      
      {/* Showcase & filter controls */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-10">
        <div className="flex flex-col md:flex-row gap-6 items-start mb-8">
          {/* Search bar */}
          <div className="relative w-full md:w-auto md:flex-grow">
            <input
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-4 py-3 bg-white dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-full text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <svg className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          
          {/* Category filter */}
          <div className="flex overflow-x-auto hide-scrollbar pb-2 md:pb-0 gap-2">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => handleFilterChange(category)}
                className={`px-4 py-2 whitespace-nowrap rounded-full text-sm ${
                  activeFilter === category
                    ? 'bg-purple-600 text-white'
                    : 'bg-white dark:bg-gray-800/50 backdrop-blur-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                }`}
              >
                {category === 'all' ? 'All Projects' : category}
              </button>
            ))}
          </div>
          
          {/* View mode switcher */}
          <div className="flex bg-white dark:bg-gray-800/50 backdrop-blur-sm rounded-full p-1 border border-gray-200 dark:border-gray-700">
            <button
              onClick={() => changeViewMode('showcase')}
              className={`p-2 rounded-full ${
                viewMode === 'showcase' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'
              }`}
              aria-label="Showcase view"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm0 8a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zm12 0a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
              </svg>
            </button>
            <button
              onClick={() => changeViewMode('grid')}
              className={`p-2 rounded-full ${
                viewMode === 'grid' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'
              }`}
              aria-label="Grid view"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => changeViewMode('timeline')}
              className={`p-2 rounded-full ${
                viewMode === 'timeline' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'
              }`}
              aria-label="Timeline view"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      
      {/* Loading state */}
      {isLoading ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex justify-center py-20">
          <div className="flex flex-col items-center">
            <svg className="animate-spin h-12 w-12 text-purple-600 dark:text-purple-400 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-gray-500 dark:text-gray-400">Loading amazing projects...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Showcase view */}
          {viewMode === 'showcase' && (
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              {searchTerm === '' && activeFilter === 'all' && (
                <div className="mb-16">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 flex items-center">
                    <span className="bg-yellow-500/20 w-8 h-8 rounded-full flex items-center justify-center mr-3">
                      <svg className="w-5 h-5 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                    </span>
                    Featured Projects
                  </h3>
                  
                  {/* Featured project showcase */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {featuredProjects.slice(0, 2).map((project, index) => (
                      <div 
                        key={project.id} 
                        className={`group relative overflow-hidden bg-white dark:bg-gray-800/30 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-gray-700 hover:border-purple-400 dark:hover:border-purple-400/50 transition-all duration-300 hover:shadow-xl hover:shadow-purple-500/10 ${
                          index === 0 ? 'lg:col-span-2' : ''
                        }`}
                      >
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"></div>
                        
                        {/* This would be an actual image in production */}
                        <div className="h-60 bg-gray-200 dark:bg-gray-700 overflow-hidden">
                          <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500 text-3xl font-bold group-hover:scale-105 transition-transform duration-500">
                            {project.title.charAt(0)}
                          </div>
                        </div>
                        
                        <div className="p-6 relative z-20">
                          <div className="flex flex-wrap gap-2 mb-3">
                            {project.tags.map(tag => (
                              <span key={tag} className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-full text-xs">
                                {tag}
                              </span>
                            ))}
                          </div>
                          
                          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-purple-500 dark:group-hover:text-purple-400 transition-colors">
                            {project.title}
                          </h3>
                          
                          <p className="text-gray-600 dark:text-gray-400 mb-4">{project.description}</p>
                          
                          <div className="flex items-center justify-between mt-2">
                            <button
                              onClick={() => handleProjectSelect(project.id)}
                              className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium inline-flex items-center"
                            >
                              View Details
                              <svg className="w-4 h-4 ml-1" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                              </svg>
                            </button>
                            
                            <div className="flex space-x-3">
                              {project.repoUrl && (
                                <a 
                                  href={project.repoUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                                  aria-label="View Code"
                                >
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.163 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.342-3.369-1.342-.454-1.155-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.337 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.16 22 16.42 22 12c0-5.523-4.477-10-10-10z" />
                                  </svg>
                                </a>
                              )}
                              
                              {project.demoUrl && (
                                <a 
                                  href={project.demoUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                                  aria-label="Live Demo"
                                >
                                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* All projects */}
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 flex items-center">
                  <span className="bg-blue-500/20 w-8 h-8 rounded-full flex items-center justify-center mr-3">
                    <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </span>
                  {activeFilter === 'all' ? 'All Projects' : `${activeFilter} Projects`}
                  {searchTerm !== '' && <span className="ml-2 text-base font-normal text-gray-500">Results for "{searchTerm}"</span>}
                </h3>
                
                {filteredProjects.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredProjects.map(project => (
                      <div 
                        key={project.id} 
                        className="group relative overflow-hidden bg-white dark:bg-gray-800/30 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-gray-700 hover:border-purple-400 dark:hover:border-purple-400/50 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/10"
                      >
                        <div className="h-40 bg-gradient-to-r from-purple-500/10 to-blue-500/10 dark:from-purple-500/20 dark:to-blue-500/20 overflow-hidden relative">
                          {/* This would be an actual image in production */}
                          <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500 text-3xl font-bold group-hover:scale-105 transition-transform duration-500">
                            {project.title.charAt(0)}
                          </div>
                          
                          <div className="absolute top-3 right-3 flex space-x-1">
                            {project.category && (
                              <span className="px-2 py-1 bg-black/30 backdrop-blur-sm text-white rounded-full text-xs">
                                {project.category}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="p-5">
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 group-hover:text-purple-500 dark:group-hover:text-purple-400 transition-colors">
                            {project.title}
                          </h3>
                          
                          <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2">{project.description}</p>
                          
                          <div className="flex flex-wrap gap-2 mb-4">
                            {project.tags.map(tag => (
                              <span key={tag} className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 rounded-full text-xs">
                                {tag}
                              </span>
                            ))}
                          </div>
                          
                          <div className="flex items-center justify-between mt-2">
                            <button
                              onClick={() => handleProjectSelect(project.id)}
                              className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 text-sm font-medium inline-flex items-center"
                            >
                              Details
                              <svg className="w-4 h-4 ml-1" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                              </svg>
                            </button>
                            
                            <div className="flex space-x-3">
                              {project.repoUrl && (
                                <a 
                                  href={project.repoUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                                  aria-label="View Code"
                                >
                                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.163 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.342-3.369-1.342-.454-1.155-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.337 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.16 22 16.42 22 12c0-5.523-4.477-10-10-10z" />
                                  </svg>
                                </a>
                              )}
                              
                              {project.demoUrl && (
                                <a 
                                  href={project.demoUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                                  aria-label="Live Demo"
                                >
                                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <svg className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="text-xl text-gray-600 dark:text-gray-400">No projects match your search</h3>
                    <button 
                      onClick={() => {setSearchTerm(""); setActiveFilter("all");}}
                      className="mt-4 text-purple-600 dark:text-purple-400 hover:text-purple-500 dark:hover:text-purple-300"
                    >
                      Clear filters
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Grid view */}
          {viewMode === 'grid' && (
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              {filteredProjects.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {filteredProjects.map(project => (
                    <div 
                      key={project.id} 
                      onClick={() => handleProjectSelect(project.id)}
                      className="group cursor-pointer bg-white dark:bg-gray-800/30 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-gray-700 hover:border-purple-400 dark:hover:border-purple-400/50 overflow-hidden transition-all p-4 flex flex-col hover:shadow-lg hover:shadow-purple-500/10"
                    >
                      <div className="flex items-center justify-center h-24 mb-3">
                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-blue-500/20 dark:from-purple-500/30 dark:to-blue-500/30 flex items-center justify-center text-purple-600 dark:text-purple-400 group-hover:scale-110 transition-transform duration-300">
                          <span className="text-2xl font-bold">{project.title.charAt(0)}</span>
                        </div>
                      </div>
                      
                      <h3 className="text-center text-gray-900 dark:text-white font-medium mb-1 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                        {project.title}
                      </h3>
                      
                      <div className="text-xs text-center text-gray-500 dark:text-gray-500 mb-2">{project.category}</div>
                      
                      <div className="flex flex-wrap justify-center gap-1 mt-auto">
                        {project.tags.slice(0, 2).map(tag => (
                          <span key={tag} className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 rounded-full text-xs">
                            {tag}
                          </span>
                        ))}
                        {project.tags.length > 2 && (
                          <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 rounded-full text-xs">
                            +{project.tags.length - 2}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <svg className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-xl text-gray-600 dark:text-gray-400">No projects match your search</h3>
                  <button 
                    onClick={() => {setSearchTerm(""); setActiveFilter("all");}}
                    className="mt-4 text-purple-600 dark:text-purple-400 hover:text-purple-500 dark:hover:text-purple-300"
                  >
                    Clear filters
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Timeline view */}
          {viewMode === 'timeline' && (
            <div className="max-w-4xl mx-auto px-4 sm:px-6">
              {filteredProjects.length > 0 ? (
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-0 md:left-1/2 transform md:translate-x-[-50%] top-0 bottom-0 w-0.5 bg-purple-200 dark:bg-purple-900/30"></div>
                  
                  <div className="space-y-12">
                    {filteredProjects.map((project, index) => (
                      <div key={project.id} className={`relative ${index % 2 === 0 ? 'md:pr-16' : 'md:pl-16 md:ml-auto'}`}>
                        {/* Timeline node */}
                        <div className="absolute left-[-8px] md:left-auto md:right-auto md:translate-x-[-50%] md:translate-y-0 top-0 w-4 h-4 rounded-full bg-purple-500 border-4 border-white dark:border-gray-900 z-10"></div>
                        
                        <div 
                          onClick={() => handleProjectSelect(project.id)}
                          className="ml-6 md:ml-0 cursor-pointer bg-white dark:bg-gray-800/30 backdrop-blur-sm rounded-xl border border-gray-200 dark:border-gray-700 hover:border-purple-400 dark:hover:border-purple-400/50 overflow-hidden transition-all hover:shadow-lg hover:shadow-purple-500/10"
                        >
                          <div className="p-5">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 hover:text-purple-500 dark:hover:text-purple-400 transition-colors">
                              {project.title}
                            </h3>
                            
                            <div className="flex flex-wrap gap-2 mb-3">
                              {project.tags.slice(0, 3).map(tag => (
                                <span key={tag} className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 rounded-full text-xs">
                                  {tag}
                                </span>
                              ))}
                            </div>
                            
                            <p className="text-gray-600 dark:text-gray-400 text-sm">{project.description}</p>
                            
                            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100 dark:border-gray-700/50">
                              <span className="text-sm text-gray-500 dark:text-gray-500">{project.category}</span>
                              
                              <div className="flex space-x-3">
                                {project.repoUrl && (
                                  <a 
                                    href={project.repoUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                                    aria-label="View Code"
                                  >
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                      <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.163 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.342-3.369-1.342-.454-1.155-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.337 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.16 22 16.42 22 12c0-5.523-4.477-10-10-10z" />
                                    </svg>
                                  </a>
                                )}
                                
                                {project.demoUrl && (
                                  <a 
                                    href={project.demoUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                                    aria-label="Live Demo"
                                  >
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-16 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <svg className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-xl text-gray-600 dark:text-gray-400">No projects match your search</h3>
                  <button 
                    onClick={() => {setSearchTerm(""); setActiveFilter("all");}}
                    className="mt-4 text-purple-600 dark:text-purple-400 hover:text-purple-500 dark:hover:text-purple-300"
                  >
                    Clear filters
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
      
      {/* Project detail modal */}
      {selectedProject && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center">
            <div className="fixed inset-0 bg-gray-900/75 dark:bg-gray-900/90 backdrop-blur-sm transition-opacity" onClick={closeProjectDetails}></div>
            
            <div className="relative inline-block bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-4xl w-full">
              <div className="absolute top-0 right-0 p-4 z-10">
                <button
                  type="button"
                  className="bg-white/20 dark:bg-gray-800/80 backdrop-blur-sm rounded-full p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                  onClick={closeProjectDetails}
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {(() => {
                const project = getSelectedProject();
                if (!project) return null;
                
                return (
                  <>
                    <div className="relative aspect-video bg-gradient-to-r from-purple-500/20 via-blue-500/20 to-indigo-500/20 dark:from-purple-500/30 dark:via-blue-500/30 dark:to-indigo-500/30">
                      <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500 text-6xl font-bold">
                        {project.title.charAt(0)}
                      </div>
                      
                      <div className="absolute bottom-4 left-4 flex flex-wrap gap-2">
                        {project.tags.map(tag => (
                          <span key={tag} className="px-3 py-1 bg-black/40 backdrop-blur-sm text-white rounded-full text-sm">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div className="p-6 sm:p-8">
                      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{project.title}</h3>
                          <div className="text-purple-600 dark:text-purple-400">{project.category}</div>
                        </div>
                        
                        <div className="flex space-x-4">
                          {project.repoUrl && (
                            <a 
                              href={project.repoUrl} 
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-lg text-gray-900 dark:text-white transition-colors"
                            >
                              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                                <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.163 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.342-3.369-1.342-.454-1.155-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.337 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.16 22 16.42 22 12c0-5.523-4.477-10-10-10z" />
                              </svg>
                              View Source
                            </a>
                          )}
                          
                          {project.demoUrl && (
                            <a 
                              href={project.demoUrl} 
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white transition-colors"
                            >
                              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                              View Live Demo
                            </a>
                          )}
                        </div>
                      </div>
                      
                      <div className="mb-8">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Overview</h4>
                        <p className="text-gray-700 dark:text-gray-300">{project.longDescription}</p>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Challenges</h4>
                          <ul className="space-y-2">
                            {project.challenges.map((challenge, index) => (
                              <li key={index} className="flex items-start">
                                <svg className="w-5 h-5 text-red-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                <span className="text-gray-700 dark:text-gray-300">{challenge}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Solutions & Highlights</h4>
                          <ul className="space-y-2">
                            {project.highlights.map((highlight, index) => (
                              <li key={index} className="flex items-start">
                                <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="text-gray-700 dark:text-gray-300">{highlight}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
      
      {/* Project skills statistics */}
      <div className="max-w-7xl mx-auto mt-20 px-4 sm:px-6">
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 flex items-center">
          <span className="bg-green-500/20 w-8 h-8 rounded-full flex items-center justify-center mr-3">
            <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </span>
          Skills & Technologies
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800/30 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-lg p-6 flex flex-col">
            <div className="text-lg text-gray-900 dark:text-white font-semibold mb-4">Programming</div>
            <div className="space-y-3 flex-grow">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Python</span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">90%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div className="bg-purple-600 dark:bg-purple-500 h-2 rounded-full" style={{ width: '90%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Java</span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">85%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div className="bg-purple-600 dark:bg-purple-500 h-2 rounded-full" style={{ width: '85%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600 dark:text-gray-400">JavaScript</span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">80%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div className="bg-purple-600 dark:bg-purple-500 h-2 rounded-full" style={{ width: '80%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600 dark:text-gray-400">C++</span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">70%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div className="bg-purple-600 dark:bg-purple-500 h-2 rounded-full" style={{ width: '70%' }}></div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800/30 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-lg p-6 flex flex-col">
            <div className="text-lg text-gray-900 dark:text-white font-semibold mb-4">Frameworks</div>
            <div className="space-y-3 flex-grow">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600 dark:text-gray-400">React</span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">85%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full" style={{ width: '85%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600 dark:text-gray-400">TensorFlow</span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">75%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full" style={{ width: '75%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600 dark:text-gray-400">scikit-learn</span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">80%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full" style={{ width: '80%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600 dark:text-gray-400">SFML</span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">65%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div className="bg-blue-600 dark:bg-blue-500 h-2 rounded-full" style={{ width: '65%' }}></div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800/30 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-lg p-6 flex flex-col">
            <div className="text-lg text-gray-900 dark:text-white font-semibold mb-4">Domains</div>
            <div className="flex flex-col gap-3">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mr-3">
                  <svg className="w-6 h-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div className="flex-grow">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Machine Learning</div>
                  <div className="text-xs text-gray-500">3 Projects</div>
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mr-3">
                  <svg className="w-6 h-6 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
                  </svg>
                </div>
                <div className="flex-grow">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Algorithms</div>
                  <div className="text-xs text-gray-500">3 Projects</div>
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mr-3">
                  <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                  </svg>
                </div>
                <div className="flex-grow">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Game Development</div>
                  <div className="text-xs text-gray-500">3 Projects</div>
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mr-3">
                  <svg className="w-6 h-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                </div>
                <div className="flex-grow">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Web Development</div>
                  <div className="text-xs text-gray-500">1 Project</div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800/30 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <div className="text-lg text-gray-900 dark:text-white font-semibold mb-4">Project Stats</div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-1">8</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Total Projects</div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-1">3</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Featured</div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-1">4</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Categories</div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-yellow-600 dark:text-yellow-400 mb-1">12</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Technologies</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Style definitions */}
      <style>
        {`
        /* Hide scrollbar for Chrome, Safari and Opera */
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        
        /* Hide scrollbar for IE, Edge and Firefox */
        .hide-scrollbar {
          -ms-overflow-style: none;  /* IE and Edge */
          scrollbar-width: none;  /* Firefox */
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        `}
      </style>
    </div>
  );
};

export default ProjectsSection;